import React, { useState, useEffect } from 'react';
import { useSignInChat } from '../orb_hooks/useSignInChat.tsx';
import { useSignUpChat } from '../orb_hooks/useSignUpChat.tsx';
import { useSignUpFormChat } from '../orb_hooks/useSignUpFormChat.tsx';
import { useInquiryChat } from '../orb_hooks/useInquiryChat.tsx';

interface SentientOrbChatProps {
  message: string;
  onStreamComplete?: () => void;
  isStreaming?: boolean;
  speed?: number;
}

export default function SentientOrbChat({ 
  message, 
  onStreamComplete,
  isStreaming = true,
  speed = 30 
}: SentientOrbChatProps) {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!message) {
      setDisplayedText('');
      setIsComplete(false);
      return;
    }

    if (!isStreaming) {
      setDisplayedText(message);
      setIsComplete(true);
      if (onStreamComplete) onStreamComplete();
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    let currentIndex = 0;

    const intervalId = setInterval(() => {
      if (currentIndex < message.length) {
        setDisplayedText(message.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(intervalId);
        setIsComplete(true);
        if (onStreamComplete) onStreamComplete();
      }
    }, speed);

    return () => clearInterval(intervalId);
  }, [message, isStreaming, speed, onStreamComplete]);

  if (!message) return null;

  return (
    <div style={{
      textAlign: 'center',
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 20px'
    }}>
      <h3 style={{
        color: '#001a33',
        fontSize: '1.2rem',
        fontWeight: '500',
        margin: 0,
        lineHeight: '1.5',
        opacity: displayedText ? 1 : 0,
        transition: 'opacity 0.3s ease'
      }}>
        {displayedText}
        {!isComplete && <span style={{ 
          animation: 'blink 1s infinite',
          marginLeft: '2px'
        }}>|</span>}
      </h3>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export function SignInChat({ 
  stage, 
  onStreamComplete,
  onStreamStart,
  isStreaming = true,
  onCharacterTyped,
  attemptCount = 0
}: { 
  stage: 'welcome' | 'invalidCredentials' | 'thirdAttempt';
  onStreamComplete?: () => void;
  onStreamStart?: () => void;
  isStreaming?: boolean;
  onCharacterTyped?: () => void;
  attemptCount?: number;
}) {
  const { displayedText, isComplete, message } = useSignInChat({
    stage,
    attemptCount,
    onStreamComplete,
    onStreamStart,
    isStreaming,
    onCharacterTyped
  });

  if (!message) return null;

  return (
    <div style={{
      textAlign: 'center',
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 20px',
      position: 'relative'
    }}>
      <h3 style={{
        color: '#001a33',
        fontSize: '1.2rem',
        fontWeight: '500',
        margin: 0,
        lineHeight: '1.5',
        opacity: displayedText ? 1 : 0,
        transition: 'opacity 0.2s ease',
        position: 'relative'
      }}>
        {displayedText}
        {stage === 'thirdAttempt' && isComplete && (
          <>
            <a 
              href="mailto:support@yourapp.com" 
              style={{
                color: '#00d9ff',
                textDecoration: 'none',
                borderBottom: '1px solid #00d9ff',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              emailing support
            </a>
            {' or '}
            <a 
              href="#forgot-password"
              style={{
                color: '#00d9ff',
                textDecoration: 'none',
                borderBottom: '1px solid #00d9ff',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              resetting your password
            </a>
            .
          </>
        )}
        {!isComplete && stage !== 'welcome' && <span style={{ 
          animation: 'blink 1s infinite',
          marginLeft: '2px'
        }}>|</span>}
      </h3>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export function SignUpChat({ 
  stage, 
  onStreamComplete,
  onStreamStart,
  isStreaming = true,
  onCharacterTyped,
  onYesClick,
  onNoClick
}: { 
  stage: 'welcome' | 'explanation' | 'acknowledgment' | 'processing' | 'success';
  onStreamComplete?: () => void;
  onStreamStart?: () => void;
  isStreaming?: boolean;
  onCharacterTyped?: () => void;
  onYesClick?: () => void;
  onNoClick?: () => void;
}) {
  const { displayedText, isComplete, message } = useSignUpChat({
    stage,
    onStreamComplete,
    onStreamStart,
    isStreaming,
    onCharacterTyped
  });

  if (!message) return null;

  const showButtons = stage === 'welcome' && isComplete;

  return (
    <div style={{
      textAlign: 'center',
      minHeight: '40px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 20px',
      position: 'relative',
      gap: '15px'
    }}>
      <h3 style={{
        color: '#001a33',
        fontSize: '1.2rem',
        fontWeight: '500',
        margin: 0,
        lineHeight: '1.5',
        opacity: displayedText ? 1 : 0,
        transition: 'opacity 0.2s ease',
        position: 'relative'
      }}>
        {displayedText}
        {!isComplete && <span style={{ 
          animation: 'blink 1s infinite',
          marginLeft: '2px'
        }}>|</span>}
      </h3>
      
      {showButtons && (
        <div style={{
          display: 'flex',
          gap: '10px',
          marginTop: '5px',
          animation: 'fadeIn 0.3s ease-in'
        }}>
          <button
            onClick={onYesClick}
            style={{
              padding: '8px 24px',
              backgroundColor: '#333333',
              color: '#F5F3ED',
              border: 'none',
              borderRadius: '20px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(51, 51, 51, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#333333';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(51, 51, 51, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#333333';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(51, 51, 51, 0.3)';
            }}
          >
            Yes, tell me more!
          </button>
          <button
            onClick={onNoClick}
            style={{
              padding: '8px 24px',
              backgroundColor: 'transparent',
              color: '#001a33',
              border: '2px solid #001a33',
              borderRadius: '20px',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#001a33';
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#001a33';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            No, I'm good
          </button>
        </div>
      )}
      
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export function SignUpFormChat({ 
  onStreamComplete,
  onStreamStart,
  isStreaming = true,
  onCharacterTyped
}: { 
  onStreamComplete?: () => void;
  onStreamStart?: () => void;
  isStreaming?: boolean;
  onCharacterTyped?: () => void;
}) {
  const { displayedText, isComplete, message } = useSignUpFormChat({
    onStreamComplete,
    onStreamStart,
    isStreaming,
    onCharacterTyped
  });

  if (!message) return null;

  return (
    <div style={{
      textAlign: 'center',
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 20px',
      position: 'relative'
    }}>
      <h3 style={{
        color: '#001a33',
        fontSize: '1.2rem',
        fontWeight: '500',
        margin: 0,
        lineHeight: '1.5',
        opacity: displayedText ? 1 : 0,
        transition: 'opacity 0.2s ease',
        position: 'relative'
      }}>
        {displayedText}
        {!isComplete && <span style={{ 
          animation: 'blink 1s infinite',
          marginLeft: '2px'
        }}>|</span>}
      </h3>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export function InquiryChat({ 
  onStreamComplete,
  onStreamStart,
  onCharacterTyped
}: { 
  onStreamComplete?: () => void;
  onStreamStart?: () => void;
  onCharacterTyped?: () => void;
}) {
  const { displayedText, isComplete } = useInquiryChat({
    onStreamComplete,
    onStreamStart,
    onCharacterTyped
  });

  return (
    <div style={{
      textAlign: 'center',
      minHeight: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 20px',
      position: 'relative',
      marginBottom: '20px'
    }}>
      <h3 style={{
        color: '#1a1a1a',
        fontSize: '1.2rem',
        fontWeight: '500',
        margin: 0,
        lineHeight: '1.5',
        opacity: displayedText ? 1 : 0,
        transition: 'opacity 0.2s ease',
        position: 'relative'
      }}>
        {displayedText}
        {!isComplete && <span style={{ 
          animation: 'blink 1s infinite',
          marginLeft: '2px'
        }}>|</span>}
      </h3>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}